import React, { useEffect, useState } from 'react';
import { Button, Descriptions, Form, Input, Select, Space, Switch, Tag, message } from 'antd';
import { CheckCircleOutlined, EditOutlined, RobotOutlined } from '@ant-design/icons';
import { CatalogItem, CatalogPayload, getProductTypes, updateCatalogItem } from '../../api/productCatalog';
import { FieldDef, ProductType, getProductTypeCatalog } from '../../api/productTypeCatalog';
import BrandLogo from '../BrandLogo';
import CardSection from './CardSection';
import { baseTypeMap, TypeFieldsFormItems } from './typeFields';

interface InfoSectionProps {
  item: CatalogItem;
  onUpdated: (item: CatalogItem) => void;
}

const renderValue = (v: unknown): React.ReactNode => {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) {
    return v.length === 0 ? '—' : (
      <Space size={4} wrap>
        {v.map((x, i) => <Tag key={i}>{String(x)}</Tag>)}
      </Space>
    );
  }
  return String(v);
};

const InfoSection: React.FC<InfoSectionProps> = ({ item, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editType, setEditType] = useState<string | undefined>();
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [allTypes, setAllTypes] = useState<ProductType[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    getProductTypes().then(setProductTypes).catch(() => {});
    getProductTypeCatalog().then(setAllTypes).catch(() => {});
  }, []);

  const primaryType = item.product_types?.[0];
  const typeFields: FieldDef[] = primaryType
    ? allTypes.find(t => t.name === primaryType)?.fields ?? []
    : [];

  const startEdit = () => {
    setEditType(primaryType ?? undefined);
    form.setFieldsValue({
      brand: item.brand,
      model_name: item.model_name,
      product_types: item.product_types ?? [],
      viscosity: item.viscosity,
      base_type: item.base_type,
      specifications: item.specifications || [],
      description: item.description,
      verified: item.verified,
      extra_data: item.extra_data || {},
    });
    setEditing(true);
  };

  const handleSave = async (values: Partial<CatalogPayload>) => {
    setSaving(true);
    try {
      const updated = await updateCatalogItem(item.id, values);
      message.success('Продукт обновлён');
      setEditing(false);
      onUpdated(updated);
    } catch {
      message.error('Ошибка обновления продукта');
    } finally {
      setSaving(false);
    }
  };

  return (
    <CardSection
      title="Информация"
      extra={
        !editing && (
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={startEdit}
            title="Редактировать"
          />
        )
      }
    >
      {editing ? (
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="brand" label="Бренд" rules={[{ required: true, message: 'Укажите бренд' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="model_name" label="Наименование" rules={[{ required: true, message: 'Укажите наименование' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="product_types" label="Тип продукта">
            <Select
              mode="multiple"
              options={productTypes.map(t => ({ value: t, label: t }))}
              allowClear
              placeholder="Выберите тип(ы)"
              onChange={(val: string[]) => setEditType(val?.[0])}
            />
          </Form.Item>
          <TypeFieldsFormItems fields={editType ? allTypes.find(t => t.name === editType)?.fields ?? [] : []} />
          {!editType && (
            <>
              <Form.Item name="viscosity" label="Вязкость">
                <Input placeholder="Например: 5W-30" />
              </Form.Item>
              <Form.Item name="base_type" label="Тип базы">
                <Select options={Object.entries(baseTypeMap).map(([value, label]) => ({ value, label }))} allowClear placeholder="Выберите тип базы" />
              </Form.Item>
              <Form.Item name="specifications" label="Спецификации">
                <Select mode="tags" placeholder="Введите и нажмите Enter" />
              </Form.Item>
            </>
          )}
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="verified" label="Проверен" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setEditing(false)}>Отмена</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Сохранить
            </Button>
          </Space>
        </Form>
      ) : (
        <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Бренд">
              <Space>
                <BrandLogo brand={item.brand} />
                {item.ai_enriched && (
                  <Tag icon={<RobotOutlined />} color="blue" style={{ fontSize: 11 }}>
                    AI {item.ai_confidence ? `${Math.round(item.ai_confidence * 100)}%` : ''}
                  </Tag>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Наименование">{item.model_name}</Descriptions.Item>
            <Descriptions.Item label="Тип продукта">
              {item.product_types && item.product_types.length > 0
                ? <Space size={4} wrap>{item.product_types.map(t => <Tag key={t}>{t}</Tag>)}</Space>
                : '—'}
            </Descriptions.Item>
            {item.viscosity && (
              <Descriptions.Item label="Вязкость">{item.viscosity}</Descriptions.Item>
            )}
            {item.base_type && (
              <Descriptions.Item label="Тип базы">{baseTypeMap[item.base_type] ?? item.base_type}</Descriptions.Item>
            )}
            {item.specifications && item.specifications.length > 0 && (
              <Descriptions.Item label="Спецификации">
                <Space size={4} wrap>{item.specifications.map(s => <Tag key={s}>{s}</Tag>)}</Space>
              </Descriptions.Item>
            )}
            {typeFields
              .filter(f => !['viscosity', 'base_type', 'specifications'].includes(f.key))
              .map(f => {
                const v = item.extra_data?.[f.key];
                if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return null;
                return (
                  <Descriptions.Item key={f.key} label={f.label}>
                    {renderValue(v)}
                  </Descriptions.Item>
                );
              })}
            {item.description && (
              <Descriptions.Item label="Описание">{item.description}</Descriptions.Item>
            )}
            <Descriptions.Item label="Статус">
              {item.verified ? (
                <Tag icon={<CheckCircleOutlined />} color="success">Проверен</Tag>
              ) : (
                <Tag color="default">Черновик</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
      )}
    </CardSection>
  );
};

export default InfoSection;
